import emptyAnalyticsState from './state'
import {
  getAnalyticsTemplatesRoutine,
  changeNewTemplateValueRoutine,
  clearNewTemplateValuesRoutine,
  changeNewTemplateFilesValueRoutine,
  removeNewTemplateFilesValueRoutine,
  getBpsWithAnalyticsRoutine,
  getAnalyticsSharingInvitationsRoutine,
  getActiveBuildingPassportsRoutine,
  attachForAllUsersRoutine,
  attachAnalyticsTemplateRoutine,
  detachAnalyticsTemplateRoutine,
  updateAttachedAnalyticsTemplateTagRequirementsRoutine,
  updateAnalyticsTemplateRoutine
} from 'features/analytics/ducks/actions'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'
import { pathOr } from 'ramda'

const applyLocalItemTagRequirements = (template, itemTagRequirements = []) => {
  if (!template || !Array.isArray(itemTagRequirements) || itemTagRequirements.length === 0) {
    return template
  }

  const requirementsByItemId = itemTagRequirements.reduce((acc, requirement) => {
    const itemId = requirement?.analytics_template_item_id
    if (!itemId) return acc
    acc[String(itemId)] = Array.isArray(requirement.file_tag_ids) ? requirement.file_tag_ids : []
    return acc
  }, {})

  const templateItems = Array.isArray(template.items) ? template.items : []
  const itemToFileType = templateItems.reduce((acc, item) => {
    const itemId = item?.id
    const fileTypeId = item?.file_type_id || item?.fileTypeId
    if (itemId && fileTypeId) {
      acc[String(itemId)] = String(fileTypeId)
    }
    return acc
  }, {})

  const requirementsByFileTypeId = itemTagRequirements.reduce((acc, requirement) => {
    const fileTypeFromPayload = requirement?.file_type_id ? String(requirement.file_type_id) : null
    const fileTypeFromItem = requirement?.analytics_template_item_id
      ? itemToFileType[String(requirement.analytics_template_item_id)]
      : null
    const fileTypeId = fileTypeFromPayload || fileTypeFromItem
    if (!fileTypeId) return acc
    const incoming = Array.isArray(requirement.file_tag_ids) ? requirement.file_tag_ids : []
    acc[fileTypeId] = Array.from(new Set([...(acc[fileTypeId] || []), ...incoming]))
    return acc
  }, {})

  const nextItems = templateItems.map(item => {
    const itemId = item?.id ? String(item.id) : null
    const fileTypeId = item?.file_type_id || item?.fileTypeId
    const selected = (itemId && requirementsByItemId[itemId]) || (fileTypeId && requirementsByFileTypeId[String(fileTypeId)])
    if (!selected) return item

    const tags = selected.map(tagId => ({
      file_tag_id: tagId,
      fileTagId: tagId,
      file_tag: { id: tagId },
      fileTag: { id: tagId }
    }))

    return {
      ...item,
      tag_requirements: tags,
      tagRequirements: tags
    }
  })

  return {
    ...template,
    items: nextItems,
    bp_item_tag_requirements_by_file_type: requirementsByFileTypeId,
    bpItemTagRequirementsByFileType: requirementsByFileTypeId
  }
}

const addPendingTemplate = (pendingByBp, bpId, templatePayload) => {
  if (!bpId || !templatePayload?.id) return pendingByBp

  const key = String(bpId)
  const existing = Array.isArray(pendingByBp[key]) ? pendingByBp[key] : []
  if (existing.some(template => String(template?.id) === String(templatePayload.id))) {
    return pendingByBp
  }

  return {
    ...pendingByBp,
    [key]: [...existing, templatePayload]
  }
}

const removePendingTemplate = (pendingByBp, bpId, templateId) => {
  if (!bpId || !templateId) return pendingByBp

  const key = String(bpId)
  const existing = Array.isArray(pendingByBp[key]) ? pendingByBp[key] : []
  const next = templateId === '__all__'
    ? []
    : existing.filter(template => String(template?.id) !== String(templateId))

  if (next.length === 0) {
    const { [key]: _removed, ...rest } = pendingByBp
    return rest
  }

  return {
    ...pendingByBp,
    [key]: next
  }
}

const clearPendingTemplatesForBps = (pendingByBp, bps = []) => {
  const loadedBpIds = new Set((bps || []).map(bp => String(bp?.id)).filter(Boolean))

  return Object.entries(pendingByBp).reduce((acc, [bpId, pendingTemplates]) => {
    if (!loadedBpIds.has(String(bpId))) {
      acc[bpId] = pendingTemplates
    }
    return acc
  }, {})
}

const getBpTemplates = bp => bp?.checklistTemplates || bp?.checklist_templates || []

const getResponseTemplates = data =>
  data?.checklistTemplates ||
  data?.checklist_templates ||
  data?.analyticsTemplates ||
  data?.analytics_templates

const LOCAL_ATTACHMENT_KEY = '__localAttachment'

const markLocalAttachment = template => ({
  ...template,
  [LOCAL_ATTACHMENT_KEY]: true
})

const mergeTemplateLists = (existingTemplates = [], incomingTemplates = []) => {
  const incomingIds = new Set(
    (incomingTemplates || []).map(template => String(template?.id)).filter(Boolean)
  )
  const localAttachments = (existingTemplates || []).filter(template =>
    template?.[LOCAL_ATTACHMENT_KEY] && !incomingIds.has(String(template?.id))
  )

  return [...(incomingTemplates || []), ...localAttachments]
}

const mergeBpForAnalytics = (existingBp, incomingBp) => {
  if (!existingBp) return incomingBp
  if (!incomingBp) return existingBp

  const incomingTemplates = getResponseTemplates(incomingBp)

  if (!Array.isArray(incomingTemplates)) {
    return {
      ...existingBp,
      ...incomingBp,
      checklistTemplates: getBpTemplates(existingBp)
    }
  }

  return {
    ...existingBp,
    ...incomingBp,
    checklistTemplates: mergeTemplateLists(
      getBpTemplates(existingBp),
      incomingTemplates
    )
  }
}

const normalizeAttachedBy = attachedBy => attachedBy
  ? {
      id: attachedBy.id,
      first_name: attachedBy.firstName || attachedBy.first_name,
      last_name: attachedBy.lastName || attachedBy.last_name,
      email: attachedBy.email
    }
  : undefined

const getAttachedTemplateFromPayload = payload => {
  const data = payload?.data || {}
  const templateId = payload?.templateId || payload?.template?.id
  const embeddedTemplate =
    data?.checklistTemplate ||
    data?.checklist_template ||
    data?.analyticsTemplate ||
    data?.analytics_template ||
    data?.template
  const dataLooksLikeTemplate = data?.id && templateId && String(data.id) === String(templateId)
  const responseTemplate = embeddedTemplate || (dataLooksLikeTemplate ? data : null)
  const baseTemplate = {
    ...(payload?.template || {}),
    ...(responseTemplate || {})
  }
  const id = baseTemplate.id || templateId

  if (!id) return null

  const attachedBy = normalizeAttachedBy(payload?.attachedBy)
  const attachedTemplate = {
    ...baseTemplate,
    id,
    visibility: payload?.visibility || baseTemplate.visibility || 'shared',
    attached_by: baseTemplate.attached_by || attachedBy,
    attachedBy: baseTemplate.attachedBy || payload?.attachedBy
  }

  return applyLocalItemTagRequirements(
    markLocalAttachment(attachedTemplate),
    payload?.item_tag_requirements || []
  )
}

const upsertAttachedTemplate = (templates, template) => {
  if (!template?.id) return templates

  const exists = templates.some(item => String(item?.id) === String(template.id))

  return exists
    ? templates.map(item =>
        String(item?.id) === String(template.id)
          ? { ...item, ...template }
          : item
      )
    : [...templates, template]
}

export default (state = emptyAnalyticsState, action) => {
  switch (action.type) {
    case getAnalyticsTemplatesRoutine.SUCCESS:
      return {
        ...state,
        templates: Array.isArray(action.payload) ? action.payload : []
      }
    case changeNewTemplateValueRoutine.SUCCESS:
      return {
        ...state,
        newTemplate: {
          ...state.newTemplate,
          [action.payload.name]: action.payload.value
        }
      }
    case changeNewTemplateFilesValueRoutine.SUCCESS:
      return {
        ...state,
        newTemplateFiles: [
          ...state.newTemplateFiles,
          action.payload
        ]
      }
    case removeNewTemplateFilesValueRoutine.SUCCESS:
      return {
        ...state,
        newTemplateFiles: state.newTemplateFiles.filter(v => v.id !== action.payload)
      }
    case clearNewTemplateValuesRoutine.SUCCESS:
      return {
        ...state,
        newTemplate: emptyAnalyticsState.newTemplate,
        newTemplateFiles: emptyAnalyticsState.newTemplateFiles
      }
    case attachAnalyticsTemplateRoutine.REQUEST: {
      const payload = action.payload || {}
      const template = payload.template
      const pendingTemplate = template
        ? {
            ...template,
            visibility: payload.visibility || template.visibility || 'shared',
            attached_by: template.attached_by || (
              payload.attachedBy
                ? {
                    id: payload.attachedBy.id,
                    first_name: payload.attachedBy.firstName,
                    last_name: payload.attachedBy.lastName,
                    email: payload.attachedBy.email
                  }
                : undefined
            )
          }
        : null

      return {
        ...state,
        attachingTemplatesByBp: addPendingTemplate(
          state.attachingTemplatesByBp || {},
          payload.bpId,
          pendingTemplate
        )
      }
    }
    case detachAnalyticsTemplateRoutine.REQUEST: {
      const payload = action.payload || {}
      const pendingTemplate = {
        id: `__detaching__-${payload.templateId || 'template'}`,
        visibility: 'shared'
      }

      return {
        ...state,
        attachingTemplatesByBp: addPendingTemplate(
          state.attachingTemplatesByBp || {},
          payload.bpId,
          pendingTemplate
        )
      }
    }
    case getBpsWithAnalyticsRoutine.SUCCESS: {
      const incoming = pathOr([], ['payload', 'data'], action)
      const currentPage = pathOr(1, ['payload', 'meta', 'currentPage'], action)
      let nextBps
      if (currentPage > 1) {
        const byId = new Map((state.bps || []).map(b => [b.id, b]))
        incoming.forEach(b => byId.set(b.id, mergeBpForAnalytics(byId.get(b.id), b)))
        nextBps = Array.from(byId.values())
      } else {
        const currentById = new Map((state.bps || []).map(b => [String(b?.id), b]))
        nextBps = incoming.map(bp => mergeBpForAnalytics(currentById.get(String(bp?.id)), bp))
      }
      return {
        ...state,
        bps: nextBps,
        attachingTemplatesByBp: clearPendingTemplatesForBps(
          state.attachingTemplatesByBp || {},
          incoming
        ),
        totalPages: pathOr(1, ['payload', 'meta', 'lastPage'], action),
        total: pathOr(0, ['payload', 'meta', 'total'], action)
      }
    }
    case fetchBpRoutine.SUCCESS: {
      const bp = action.payload
      if (!bp?.id) return state

      const nextBps = (state.bps || []).map(item =>
        String(item?.id) === String(bp.id) ? mergeBpForAnalytics(item, bp) : item
      )

      return {
        ...state,
        bps: nextBps,
        attachingTemplatesByBp: removePendingTemplate(
          state.attachingTemplatesByBp || {},
          bp.id,
          '__all__'
        )
      }
    }
    case attachAnalyticsTemplateRoutine.SUCCESS: {
      const payload = action.payload || {}
      const bpId = payload.bpId
      const data = payload.data || {}
      const responseTemplates = getResponseTemplates(data)
      const attachedTemplate = getAttachedTemplateFromPayload(payload)
      if (!bpId) {
        return state
      }

      return {
        ...state,
        bps: state.bps.map(bp => {
          if (String(bp?.id) !== String(bpId)) return bp

          if (data && String(data.id) === String(bpId) && Array.isArray(responseTemplates)) {
            return mergeBpForAnalytics(bp, data)
          }

          if (Array.isArray(responseTemplates)) {
            return {
              ...bp,
              checklistTemplates: mergeTemplateLists(
                getBpTemplates(bp),
                responseTemplates
              )
            }
          }

          return {
            ...bp,
            checklistTemplates: upsertAttachedTemplate(
              getBpTemplates(bp),
              attachedTemplate
            )
          }
        }),
        attachingTemplatesByBp: removePendingTemplate(
          state.attachingTemplatesByBp || {},
          bpId,
          attachedTemplate?.id || payload.templateId
        )
      }
    }
    case attachAnalyticsTemplateRoutine.FAILURE: {
      const payload = action.payload || {}
      return {
        ...state,
        attachingTemplatesByBp: removePendingTemplate(
          state.attachingTemplatesByBp || {},
          payload.bpId,
          payload.template?.id || payload.templateId
        )
      }
    }
    case detachAnalyticsTemplateRoutine.SUCCESS: {
      const payload = action.payload || {}
      const bpId = payload.bpId
      const templateId = payload.templateId
      if (!bpId || !templateId) return state

      return {
        ...state,
        bps: (state.bps || []).map(bp => {
          if (String(bp?.id) !== String(bpId)) return bp

          const templates = bp.checklistTemplates || bp.checklist_templates || []
          const nextTemplates = templates.filter(
            template => String(template?.id) !== String(templateId)
          )

          return {
            ...bp,
            checklistTemplates: nextTemplates
          }
        })
      }
    }
    case updateAttachedAnalyticsTemplateTagRequirementsRoutine.SUCCESS: {
      const payload = action.payload || {}
      const bpId = payload.bpId
      const templateId = payload.templateId
      if (!bpId || !templateId) {
        return state
      }

      return {
        ...state,
        bps: state.bps.map(bp => {
          if (String(bp?.id) !== String(bpId)) return bp

          const templates = getBpTemplates(bp)
          const updatedTemplates = templates.map(template =>
            String(template.id) === String(templateId)
              ? applyLocalItemTagRequirements(template, payload.item_tag_requirements)
              : template
          )

          return {
            ...bp,
            checklistTemplates: updatedTemplates
          }
        })
      }
    }
    case updateAnalyticsTemplateRoutine.SUCCESS: {
      const updated = action.payload
      if (!updated?.id) return state

      return {
        ...state,
        templates: (state.templates || []).map(template =>
          template.id === updated.id
            ? { ...template, ...updated }
            : template
        )
      }
    }
    case getAnalyticsSharingInvitationsRoutine.SUCCESS:
      return {
        ...state,
        invitations: action.payload
      }
    case getActiveBuildingPassportsRoutine.SUCCESS:
      return {
        ...state,
        activeBuildingPassports: Array.isArray(action.payload?.data) ? action.payload.data : [],
      };

    case attachForAllUsersRoutine.SUCCESS:
      return {
        ...state,
        attachForAllUsersSuccess: true
      };
    default:
      return state
  }
}
